import React, { useState } from 'react';

import { AutocompleteItem, Button, Checkbox, Group, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import TaxRateAutoComplete, {
  TaxRateAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/TaxRateAutoComplete';
import MoneyField from 'components/MoneyField';
import ImageUpload from 'components/upload/ImageUpload';
import { useLocalization } from 'context/localization-context';
import useCreateProduct from 'hooks/product/useCreateProduct';
import useProduct from 'hooks/product/useProduct';
import useUpdateProduct from 'hooks/product/useUpdateProduct';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  name: string;
  isFavorite: boolean;
  price: number;
  taxRate: TaxRateAutoCompleteValue;
  file: File | null;
}

const DrawerRetailForm = ({
  opened,
  productId,
  closeHandler,
  handleResetCart,
  permission = true,
}: {
  opened: boolean;
  productId?: string;
  closeHandler: () => void;
  handleResetCart: () => void;
  permission?: boolean;
}): JSX.Element => {
  const { country } = useLocalization();

  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const { successNotification, errorNotification } = notificationHandler();

  const { data, isLoading, isSuccess } = useProduct(productId);

  const [retailPictureUrl, setRetailPictureUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      isFavorite: false,
      price: 0,
      taxRate: {
        taxRateId: '',
        value: '',
        label: '',
      },
      file: null,
    },

    validate: {
      name: value => (value.trim().length > 0 ? null : 'Name is required'),
      price: value => (value >= 0 ? null : 'Price must be provided'),
    },

    transformValues: values => ({
      ...values,
      price: Math.round(values.price * 100),
    }),
  });

  const handleImageChange = (file: File): void => {
    setUploading(true);
    if (file && file.size > 5000000) {
      errorNotification('The selected file is too large. Please select a file smaller than 5MB.');
    } else if (file) {
      setRetailPictureUrl(URL.createObjectURL(file));
      form.setValues({ ...form.values, file });
    }
    setUploading(false);
  };

  const handleTaxRateChange = (item: AutocompleteItem | null): void => {
    const newValue = {
      taxRateId: item ? item.taxRateId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('taxRate', newValue);
  };

  const handleCloseAndReset = (): void => {
    setRetailPictureUrl(null);
    form.reset();
    closeHandler();
  };

  const handleSubmit = (values: FormValues): void => {
    const type: 'RETAIL' = 'RETAIL';
    const payload = {
      ...values,
      isFavorite: values.isFavorite,
      price: values.price,
      productId: productId || uuidv4(),
      taxRate: form.values.taxRate.taxRateId || null,
      type,
    };

    if (productId) {
      updateMutation.mutate(payload, {
        onError: error => {
          errorNotification(error.message);
        },
        onSuccess: () => {
          successNotification('Retail Item Updated');
          handleCloseAndReset();
          handleResetCart();
        },
      });
    } else {
      createMutation.mutate(payload, {
        onError: error => {
          errorNotification(error.message);
        },
        onSuccess: () => {
          successNotification('Retail Item Created');
          handleCloseAndReset();
        },
      });
    }
  };

  useShallowEffect(() => {
    if (data?.data && !isLoading && isSuccess && opened) {
      setRetailPictureUrl(data.data.image);
      form.setValues({
        name: data.data.name,
        isFavorite: data.data.isFavorite,
        price: (data.data.price ?? 0) / 100,
        taxRate: {
          taxRateId: data.data?.taxRate?.taxRateId || '',
          value: data.data?.taxRate?.taxRateId || '',
          label: data.data?.taxRate?.name || '',
        },
      });
    }
  }, [data?.data, isLoading, isSuccess, opened, data?.data.image]);

  return (
    <Drawer
      title={
        productId ? (
          <Title order={3}>{permission ? 'Edit' : 'View'} Retail Item</Title>
        ) : (
          <Title order={3}>Create Retail Item</Title>
        )
      }
      opened={opened}
      onClose={handleCloseAndReset}
      size={650}
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form='retail-form'
            type='submit'
            loading={createMutation.isLoading || updateMutation.isLoading}
            disabled={!permission || uploading || isLoading}
            data-cy='saveRetailItemButton'
          >
            Save
          </Button>
        </Group>
      }
    >
      <form onSubmit={form.onSubmit(handleSubmit)} id='retail-form'>
        <Group grow align='top' spacing='xl'>
          <Stack>
            <TextInput
              {...form.getInputProps('name')}
              label='Name'
              placeholder='Enter name'
              disabled={!permission}
              required
              data-cy='retailItemName'
            />
            <Checkbox
              {...form.getInputProps('isFavorite')}
              checked={form.values?.isFavorite}
              label='Add To Favorites'
              disabled={!permission}
              data-cy='retailFavoriteButton'
            />
            <MoneyField
              country={country}
              {...form.getInputProps('price')}
              label='Price'
              disabled={!permission}
              required
              data-cy='retailPriceInput'
            />
            <TaxRateAutoComplete
              disabled={!permission}
              label='Tax Rate'
              placeholder='Set tax rate'
              value={form.values.taxRate}
              onChange={handleTaxRateChange}
              error={form.errors.taxRate}
              zIndex={99999}
              data-cy='retailTaxRateInput'
            />
          </Stack>
          <Stack my='xs'>
            <ImageUpload
              handleFileChange={files => handleImageChange(files[0])}
              disabled={!permission}
              imgSrc={retailPictureUrl || ''}
              loading={uploading}
              width={'100%'}
              height={'100%'}
            />
          </Stack>
        </Group>
      </form>
    </Drawer>
  );
};

export default DrawerRetailForm;
